import {booleanAttribute, Component, inject, Input, OnInit, ViewChild} from '@angular/core';
import {KENDO_BUTTONS} from '@progress/kendo-angular-buttons';
import {OAuthService} from 'angular-oauth2-oidc';

import {ValLangueService} from '../../../../../val-definitions-communes-lib/src/public-api';
import {AsPreference, codePreferenceLangue, ServicesPreferencesService} from '../../../../../val-ammon-services/val-services-lib/src/public-api';

import {BoiteDialogueGeneriqueComponent} from '../boite-dialogue-generique/boite-dialogue-generique.component';
import {FonteIconeComponent} from '../fonte-icone/fonte-icone.component';
import {PanneauInformationComponent} from '../panneau-information/panneau-information.component';
import {SimpleBoutonComponent} from '../simple-bouton/simple-bouton.component';
import {SimpleLibelleComponent} from '../simple-libelle/simple-libelle.component';
import {SimplePanelComponent} from '../simple-panel/simple-panel.component';

//Préfixe des préférences d’état du bandeau des recherches (val-ammon-search-lib, bandeau-recherche) :
//une préférence par recherche dont le bandeau est replié, le défaut étant ouvert
const prefixeBandeauxRecherche = "search.bandeau.";

//Message affiché à l’utilisateur après une action
export class MessageMesPreferences
{
  //Création
  //  Paramètres
  //    texte  : Texte
  //    erreur : Indique s’il s’agit d’une erreur
  constructor (texte: string, erreur: boolean)
  {
    this.texte = texte;
    this.erreur = erreur;
  }

  //Texte
  public texte: string;

  //Indique s’il s’agit d’une erreur
  public erreur: boolean;
}

//Composant « Mes préférences » de l’utilisateur courant (préférences Ammon Services).
//Affiche un catalogue connu de préférences, chacune avec sa valeur courante : la langue de l’interface,
//modifiable, puis l’état des bandeaux des recherches. Le bouton « Rétablir les valeurs par défaut »
//supprime toutes les préférences de l’utilisateur, après confirmation.
//  Utilisation : <val-mes-preferences></val-mes-preferences>, chargement à l’affichage
@Component (
{
  selector: 'val-mes-preferences',
  imports:
  [
    KENDO_BUTTONS,
    BoiteDialogueGeneriqueComponent,
    FonteIconeComponent,
    PanneauInformationComponent,
    SimpleBoutonComponent,
    SimpleLibelleComponent,
    SimplePanelComponent
  ],
  templateUrl: './mes-preferences.component.html',
  styleUrl: './mes-preferences.component.scss'
})
export class MesPreferencesComponent implements OnInit
{
  private servicesPreferencesService = inject (ServicesPreferencesService);
  private valLangueService = inject (ValLangueService);
  private oAuthService = inject (OAuthService, {optional: true});

  //Création
  constructor ()
  {
    this.chargementAutomatique = true;
    this.chargementEnCours = false;
    this.erreurChargement = false;
    this.operationEnCours = false;
    this.preferences = [];
    this.message = null;
  }

  //Boîte de dialogue de confirmation du retour aux valeurs par défaut
  @ViewChild ("boiteDialogueConfirmation")
  public boiteDialogueConfirmation!: BoiteDialogueGeneriqueComponent<boolean>;

  //Indique si les préférences sont chargées dès l’affichage du composant
  @Input ({transform: booleanAttribute})
  public chargementAutomatique: boolean;

  //Indique que le chargement est en cours
  public chargementEnCours: boolean;

  //Indique que le chargement a échoué
  public erreurChargement: boolean;

  //Indique qu’une modification est en cours
  public operationEnCours: boolean;

  //Préférences enregistrées de l’utilisateur : seuls les écarts à la valeur par défaut
  public preferences: AsPreference[];

  //Dernier message de succès ou d’erreur
  public message: MessageMesPreferences | null;

  //Langues proposées par l’application
  public get Langues () : string[]
  {
    return this.valLangueService.Langues;
  }

  //Langue active
  public get LangueActive () : string
  {
    return this.valLangueService.LangueActive;
  }

  //Indique si la langue a été choisie par l’utilisateur
  public get LanguePersonnalisee () : boolean
  {
    return this.preferences.some (preference => preference.code === codePreferenceLangue);
  }

  //Nombre de recherches dont le bandeau est replié
  public get NombreBandeauxReplies () : number
  {
    return this.preferences.filter (preference => preference.code.startsWith (prefixeBandeauxRecherche)).length;
  }

  //Initialisation
  public ngOnInit () : void
  {
    if (this.chargementAutomatique)
      void this.Charger ();
  }

  //Traduit un texte de l’écran
  //  Paramètres
  //    cle    : Clé, sous valcomposantslib.mes-preferences
  //    defaut : Texte français, affiché si l’application ne porte pas la clé
  public Texte (cle: string, defaut: string) : string
  {
    return this.valLangueService.TraduireOuDefaut (`valcomposantslib.mes-preferences.${cle}`, defaut);
  }

  //Libellé d’une langue, dans sa propre langue
  //  Paramètres
  //    langue : Code de la langue
  public LibelleLangue (langue: string) : string
  {
    return this.valLangueService.Libelle (langue);
  }

  //Valeur affichée pour les bandeaux des recherches
  public TexteBandeaux () : string
  {
    const nombre: number = this.NombreBandeauxReplies;

    if (nombre === 0)
      return this.Texte ("bandeaux.defaut", "Ouverts");

    return this.Texte ("bandeaux.replies", "Repliés sur {n} recherche(s)").replace ("{n}", String (nombre));
  }

  //Charge les préférences de l’utilisateur
  public async Charger () : Promise<void>
  {
    this.chargementEnCours = true;
    this.erreurChargement = false;

    try
    {
      const valApiResultat = await this.servicesPreferencesService.RetournerPreferences ();

      if (valApiResultat?.status !== "success")
        throw new Error (valApiResultat?.message ?? "");

      this.preferences = valApiResultat.resultat ?? [];
    }
    catch
    {
      this.preferences = [];
      this.erreurChargement = true;
    }
    finally
    {
      this.chargementEnCours = false;
    }
  }

  //Change la langue de l’interface et l’enregistre pour l’utilisateur
  //  Paramètres
  //    langue : Code de la langue
  public async ChangerLangue (langue: string) : Promise<void>
  {
    if (this.operationEnCours || langue === this.LangueActive)
      return;

    this.operationEnCours = true;
    this.message = null;

    try
    {
      if (await this.valLangueService.Changer (langue))
      {
        this.preferences = [...this.preferences.filter (preference => preference.code !== codePreferenceLangue),
                            {code: codePreferenceLangue, valeur: {langue: langue}, quandModif: new Date ().toISOString ()}];

        this.Afficher (this.Texte ("succes.langue", "Langue enregistrée."), false);
      }
      else
        this.Afficher (this.Texte ("erreur.langue", "La langue est appliquée, mais n’a pas pu être enregistrée pour votre compte. Réessayez plus tard."), true);
    }
    finally
    {
      this.operationEnCours = false;
    }
  }

  //Supprime toutes les préférences de l’utilisateur après confirmation, puis réapplique les valeurs par défaut :
  //la langue revient à celle du compte (claim locale), à défaut à celle du navigateur
  public async Retablir () : Promise<void>
  {
    if (this.operationEnCours || !await this.boiteDialogueConfirmation.Ouvrir ())
      return;

    this.operationEnCours = true;
    this.message = null;

    try
    {
      const valApiResultat = await this.servicesPreferencesService.SupprimerPreferences ();

      if (valApiResultat?.status !== "success")
        throw new Error (valApiResultat?.message ?? "");

      this.preferences = [];
      this.valLangueService.RetablirDefaut (this.LangueCompte ());

      this.Afficher (this.Texte ("succes.retablir", "Les valeurs par défaut sont rétablies."), false);
    }
    catch
    {
      this.Afficher (this.Texte ("erreur.retablir", "Les valeurs par défaut n’ont pas pu être rétablies. Réessayez plus tard."), true);
    }
    finally
    {
      this.operationEnCours = false;
    }
  }

  //Ferme la boîte de dialogue de confirmation
  //  Paramètres
  //    confirme : Indique si l’utilisateur confirme
  public FermerDialogueConfirmation (confirme: boolean) : void
  {
    this.boiteDialogueConfirmation.fonctionRetour (confirme);
    this.boiteDialogueConfirmation.Fermer ();
  }

  //Ferme le message affiché
  public FermerMessage () : void
  {
    this.message = null;
  }

  //Langue du compte, portée par le claim locale du jeton
  private LangueCompte () : string | null
  {
    const locale: unknown = this.oAuthService?.getIdentityClaims ()?.['locale'];

    return typeof locale === "string" ? locale : null;
  }

  //Affiche un message
  //  Paramètres
  //    texte  : Texte
  //    erreur : Indique s’il s’agit d’une erreur
  private Afficher (texte: string, erreur: boolean) : void
  {
    this.message = new MessageMesPreferences (texte, erreur);
  }
}
